import { useEffect, useState } from 'react';

import { TextField, CircularProgress } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import useDebouncedEffect from 'hooks/useDebouncedEffect';

import { FaSearch } from 'react-icons/fa';
import {
  Card,
  Col,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from 'reactstrap';
import api from 'services/api';
import { iMaterial } from 'views/pages/Materiais';

interface Props {
  descricao?: string;
  children?: any;
  handleSelecionarItem: (itemDomsge: iAutoCompleteItem | null) => void;
  hideTitle?: boolean;
  maxWidth?: string;
  noAutoFocus?: boolean;
  emprCodigo?: string | undefined;
  label?: string | undefined;
  geral?: boolean;
  valorInput?: number | null;
  ignorarMargem?: boolean;
  usarDiv?: boolean;
  compact?: boolean;
  obrigatorio?: boolean;
}

export interface iAutoCompleteItem {
  id: number;
  codigo: string;
  descricao: string;
}

export default function AutocompleteItem({
  descricao,
  children,
  handleSelecionarItem,
  hideTitle,
  maxWidth,
  noAutoFocus,
  valorInput,
  label,
  usarDiv = false,
  compact = false,
  ignorarMargem = false,
  obrigatorio = false,
  emprCodigo,
  geral,
}: Props): JSX.Element {
  const [loadingItens, setLoadingItens] = useState(false);
  const [loadingLista, setLoadingLista] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([] as iAutoCompleteItem[]);
  const [open, setOpen] = useState(false);
  const [itemSelecionado, setItemSelecionado] =
    useState<iAutoCompleteItem | null>(options[0]);
  const [listaMaterial, setListaMaterial] = useState([] as iMaterial[]);

  async function carregarDados() {
    setLoadingLista(true);

    const response = await api.get('/material');

    const lista = response.data as iMaterial[];

    setListaMaterial(lista.filter(item => item.isAtivo));
    setLoadingLista(false);
  }

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    if (!valorInput || inputValue !== '') return;

    const carregarMaterial = async () => {
      const response = await api.get(`/material/${valorInput}`);
      setInputValue(response.data.descricao);
      setItemSelecionado(response.data);
    };

    carregarMaterial();
  }, [valorInput]);

  useDebouncedEffect(
    () => {
      let active = true;

      if (!open || loadingItens || inputValue.length < 3) {
        return undefined;
      }

      (async () => {
        try {
          setLoadingItens(true);

          const novaLista = listaMaterial.filter(item => {
            const busca = inputValue?.toString().trim().toUpperCase();

            const diteCodigo = item.diteCodigo?.toString().trim().toUpperCase();
            const codigoSap = item.codigoSap?.toString().trim().toUpperCase();
            const descricao2 = item.descricao?.toString().trim().toUpperCase();

            return (
              diteCodigo?.includes(busca) ||
              codigoSap?.includes(busca) ||
              descricao2?.includes(busca)
            );
          });

          setOptions(
            novaLista.map(item => {
              const desc = `${item.codigoSap} ${
                item.diteCodigo ? `(SGE: ${item.diteCodigo})` : ''
              } - ${item.descricao}`;
              return {
                id: item.id,
                codigo: item.codigoSap || item.diteCodigo,
                descricao: desc,
              };
            }) as iAutoCompleteItem[],
          );

          // Modificado temporariamente. Será deixado dinâmico na sprint 44
          /* const codigoEmpresa = emprCodigo !== '' ? emprCodigo : '710';

          const response = await api.get(`/integracao/item`, {
            params: {
              search: inputValue,
              emprCodigo: codigoEmpresa ?? '',
              geral,
            },
          });

          console.log(response.data);

          if (active) {
            setOptions(response.data);
          } */
        } finally {
          setLoadingItens(false);
        }
      })();

      return () => {
        active = false;
      };
    },
    1000,
    [inputValue],
  );

  useEffect(() => {
    if (!itemSelecionado) {
      setInputValue('');
    }

    setLoadingItens(false);
    setOpen(false);
    setOptions([]);
  }, [itemSelecionado]);

  const Wrapper = usarDiv ? 'div' : Card;

  return (
    <Wrapper className={compact ? 'mb-1' : 'shadow mt-3 mb-1'}>
      <Col className={`${ignorarMargem ? 'mt-0' : 'mt-4'} p-0`}>
        <FormGroup className="mb-4">
          {!hideTitle && (
            <h5 className="mb-3">
              {descricao ? `${descricao} | ` : ''} Vincular item
            </h5>
          )}

          <InputGroup className="input-group-alternative">
            {!compact && (
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <FaSearch />
                </InputGroupText>
              </InputGroupAddon>
            )}

            <Autocomplete
              className={`${ignorarMargem ? 'mt-0' : 'mt-1'}`}
              style={{ maxWidth: maxWidth || '51vw' }}
              fullWidth
              size="small"
              open={open}
              options={options}
              disabled={loadingLista}
              loading={loadingItens || loadingLista}
              inputValue={inputValue}
              onOpen={() => {
                setOpen(true);
              }}
              onClose={() => {
                setOpen(false);
              }}
              getOptionSelected={(option: iAutoCompleteItem, value) =>
                option.id === value.id
              }
              getOptionLabel={option =>
                option?.codigo ? `${option.descricao}` : ''
              }
              loadingText="Carregando..."
              noOptionsText="Nada encontrado"
              value={itemSelecionado}
              clearOnBlur
              clearOnEscape
              disableClearable={false}
              onChange={(_, itemDomsge) => {
                setItemSelecionado(itemDomsge);
                handleSelecionarItem(itemDomsge);
              }}
              filterOptions={(options2: any) => options2}
              onInputChange={(_, newInputValue) => {
                if (!newInputValue) return;

                setInputValue(newInputValue);
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  inputRef={input => input && !noAutoFocus && input.focus()}
                  size="small"
                  required={obrigatorio}
                  label={
                    loadingLista ? (
                      <>
                        Carregando...{' '}
                        <CircularProgress color="inherit" size={13} />
                      </>
                    ) : (
                      label || 'Pesquise um item...'
                    )
                  }
                  variant="outlined"
                  InputLabelProps={{ style: { fontSize: 13 } }}
                  InputProps={{
                    ...params.InputProps,
                    style: { fontSize: 13 },
                  }}
                />
              )}
            />

            {children}
          </InputGroup>
        </FormGroup>
      </Col>
    </Wrapper>
  );
}
